import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Star, Clock, MapPin } from "lucide-react";
import { useApp } from "@/contexts/AppContext";

export default function MovieDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { city } = useApp();
  const [movie, setMovie] = useState(null);
  const [activeDate, setActiveDate] = useState(null);

  useEffect(() => {
    api.get(`/movies/${id}`).then((r) => {
      setMovie(r.data);
      const dates = [...new Set((r.data.showtimes || []).map((s) => s.date))].sort();
      setActiveDate(dates[0]);
    });
  }, [id]);

  if (!movie) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const dates = [...new Set((movie.showtimes || []).map((s) => s.date))].sort();
  const showtimesForDate = (movie.showtimes || []).filter((s) => s.date === activeDate && s.city === city);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden border-b border-border">
        <img src={movie.poster} alt="" className="absolute inset-0 w-full h-full object-cover blur-3xl opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        <div className="relative page-shell py-10 md:py-14 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <img src={movie.poster} alt={movie.title} className="w-full max-w-xs aspect-[2/3] rounded-2xl object-cover border border-border" />
          </div>
          <div className="md:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.3em] font-black text-primary">{movie.language}</p>
            <h1 className="font-display text-4xl md:text-5xl font-black mt-2 leading-tight">{movie.title}</h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Star className="h-4 w-4 text-primary fill-primary" /> {movie.rating}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {movie.duration}</span>
              <span>{movie.genre}</span>
            </div>
            <p className="mt-4 max-w-2xl text-foreground/90">{movie.synopsis}</p>
            <div className="mt-4 text-sm">
              <p><span className="text-muted-foreground">Director:</span> {movie.director}</p>
              <p><span className="text-muted-foreground">Cast:</span> {movie.cast?.join(", ")}</p>
            </div>
          </div>
        </div>
      </section>

      <main className="page-shell py-10">
        <h2 className="font-display text-2xl font-extrabold mb-4 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" /> Showtimes in {city}
        </h2>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-5" data-testid="showtime-dates">
          {dates.map((d) => (
            <button
              key={d}
              data-testid={`date-${d}`}
              onClick={() => setActiveDate(d)}
              className={`pill ${activeDate === d ? "pill-active" : "hover:bg-muted"}`}
            >
              {new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
            </button>
          ))}
        </div>

        {showtimesForDate.length === 0 && (
          <p className="text-sm text-muted-foreground">No showtimes in {city} for this date. Try another city or date.</p>
        )}

        <div className="space-y-4">
          {showtimesForDate.reduce((acc, s) => {
            (acc[s.venue] ||= []).push(s); return acc;
          }, {}) && Object.entries(showtimesForDate.reduce((acc, s) => { (acc[s.venue] ||= []).push(s); return acc; }, {})).map(([venue, list]) => (
            <div key={venue} className="rounded-2xl border border-border p-5">
              <p className="font-display font-bold">{venue}</p>
              <p className="text-xs text-muted-foreground mb-3">£{list[0].price_standard?.toFixed(2)} standard · £{list[0].price_premium?.toFixed(2)} premium</p>
              <div className="flex flex-wrap gap-2">
                {list.map((s) => (
                  <Button
                    key={s.showtime_id}
                    variant="outline"
                    size="sm"
                    data-testid={`showtime-${s.showtime_id}`}
                    onClick={() => navigate(`/booking/movie/${movie.movie_id}/${s.showtime_id}`)}
                    className="rounded-full font-bold"
                  >
                    {s.time}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
